import { VerificationQuestion } from 'types/verification.types';

export enum MessageType {
  USER_FILE = 'user_file',
  USER_TEXT = 'user_text',
  ASSISTANT = 'assistant',
  THINKING = 'thinking',
  SYSTEM = 'system',
  PROCESSING = 'processing',
  RESULT = 'result',
  CLASSIFICATION_RESULT = 'classification_result',
  ERROR = 'error',
  VERIFICATION_QUESTION = 'verification_question',
  VERIFICATION_RESPONSE = 'verification_response',
  REASONING = 'reasoning',
  AGENT_QUESTION = 'agent_question',
}

export enum ProcessingStatus {
  IDLE = 'idle',
  UPLOADING = 'uploading',
  QUEUED = 'queued',
  PROCESSING = 'processing',
  VERIFICATION_IN_PROGRESS = 'verification_in_progress',
  VERIFICATION_COMPLETED = 'verification_completed',
  FINALIZING = 'finalizing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface BaseMessage {
  id: number;
  type: MessageType;
  timestamp: Date;
  content?: string;
}

export interface UserFileMessage extends BaseMessage {
  type: MessageType.USER_FILE;
  fileName: string;
  fileSize: number;
}

export interface FileExtractionStatusItem {
  file_name: string;
  file_index: number;
  status: 'pending' | 'extracting' | 'ready' | 'failed';
  error?: string;
}

export interface ProcessingMessage extends BaseMessage {
  type: MessageType.PROCESSING;
  jobId?: string;
  status?: string;
  progress?: number;
  queuePosition?: number;
  fileExtractionStatus?: FileExtractionStatusItem[];
  pipelineStage?: string;
}

export interface HTSClassificationData {
  success?: boolean;
  primary_hts_code: string;
  hts_description?: string;
  product_category?: string;
  country_of_origin?: string;
  chapter?: string;
  chapter_description?: string;
  notes?: string[];
  assumptions?: string[];
  confidence_score?: number;
  duty_rate?: string;
  base_duty?: string;
  hts_duty_rate?: string;
  additional_tariff?: string;
  total_duty?: string;
  section_301_tariff?: string;
  section_232_tariff?: string;
  section_122_tariff?: string;
  additional_classifications?: Array<{
    hts_code: string;
    description: string;
    confidence: number;
    reason_rejected?: string;
  }>;
  // Tariff details
  topic?: string;
  jurisdiction?: string;
  tariff_percentage?: string;
  tariff_description?: string;
  tariff_confidence_score?: number;
  additional_tariffs_found?: Array<{
    tariff_type?: string;
    rate?: string;
    description?: string;
  }>;
  // Source details
  source_title?: string;
  source_url?: string;
  publish_date?: string;
  effective_date?: string;
  source_type?: string;
  evidence_quote?: string;
  // Agent metadata
  cross_rulings_consulted?: string[];
  classification_method?: string;
  gri_rules_applied?: string[];
  sources_consulted?: string[];
  source_references?: Array<{
    label: string;
    url?: string | null;
    source_type?: string;
  }>;
}

// Feedback Types
export interface FeedbackRequest {
  classification_id: string;
  is_positive: boolean;
}

export interface FeedbackResponse {
  feedback_id: string;
  classification_id: string;
  user_id: string;
  is_positive: boolean;
  created_at: string;
}

// Reclassification Types
export interface ReclassificationRequest {
  reason: string;
  triggered_by?: string;
  use_previous_context?: boolean;
  user_message: string;
  conversation_id?: string;
}

export interface ReclassificationResponse {
  job_id: string;
  document_id: string;
  file_id: string;
  status: string;
  message: string;
  previous_version?: number;
  new_version?: number;
  queue_position?: number;
}

export interface TariffCitation {
  url: string;
  source_name: string;
  source_text?: string;
  is_authoritative: boolean;
  retrieved_at: string;
  publication_date: string | null;
  age_days: number | null;
}

export interface TariffSectionResult {
  program: string;
  status: 'applicable' | 'not_applicable' | 'uncertain';
  rate: string | null;
  rate_display: string;
  basis?: string;
  confidence: string;
  confidence_score: number;
  conflicts_detected: boolean;
  conflict_detail?: string;
  sources_checked: number;
  search_queries_used?: string[];
  warnings: string[];
  citations: TariffCitation[];
}

export interface TariffBaseDuty {
  rate: string;
  rate_display: string;
  duty_amount: string;
  confidence: string;
  confidence_score: number;
  conflicts_detected: boolean;
  sources_checked: number;
  warnings: string[];
  citations: TariffCitation[];
}

export interface TariffAdditionalTariffs {
  applicable_programs: string[];
  total_additional_rate: string;
  stacking_detected: boolean;
  section_122?: TariffSectionResult;
  section_232?: TariffSectionResult;
  section_301?: TariffSectionResult;
  warnings: string[];
  all_citations?: TariffCitation[];
}

/**
 * Deterministic tariff determination result (the shape emitted by the backend's
 * DeterministicDetermination.to_quick_check_dict()). Replaces the legacy
 * FinalDutyResult shape. The legacy interfaces above are retained for any
 * historical conversations persisted in the old format.
 */

export interface TariffBaseMfn {
  ad_valorem_pct?: number | null;
  rate_display: string;
}

export interface TariffAddon {
  applicable: boolean | null;
  rate_pct: number | null;
  rate_display: string;
  basis: string;
  confidence: string;
  as_of: string | null;
  excluded: boolean;
  reason: string | null;
}

export interface TariffFlag {
  kind: 'addcvd' | 'trq' | 'staleness' | 'value_required' | 'scope' | string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  detail?: Record<string, any>;
}

export interface TariffDeterminationData {
  mode: string;
  path?: 'deterministic' | 'web_search';
  hts_code: string;
  origin_country: string;
  as_of_date: string;
  base_mfn_rate: TariffBaseMfn | null;
  addons: Record<string, TariffAddon>;
  total_additional_pct: number;
  estimated_total_duty_pct: number;
  has_specific_components?: boolean;
  compound_rates_present?: boolean;
  stacking_order?: string[];
  customs_value?: number | null;
  duty_owed?: number | null;
  duty_owed_resolvable?: boolean;
  display_summary: string;
  flags: TariffFlag[];
  excluded_from_total?: string[];
  warnings: string[];
  degraded?: boolean;
  degraded_reason?: string | null;
  informational_only?: string;
  tariff_determination_ms?: number;
  /** Attached only on the narrow web-search exception path. */
  web_search?: Record<string, any>;
}

export interface ResultData {
  filename?: string;
  classification_id?: string;
  document_id?: string;
  hts_classification?: HTSClassificationData;
  tariff_determination?: TariffDeterminationData;
  tariff_loading?: boolean;
  sections?: Record<string, any>;
  extracted_data?: {
    gsp?: {
      country_of_origin?: {
        value?: string;
        confidence?: number;
        source?: string;
        verified?: boolean;
      };
    };
    [key: string]: any;
  };
}

export interface ResultMessage extends BaseMessage {
  type: MessageType.RESULT;
  result: ResultData;
}

export interface ClassificationResultMessage extends BaseMessage {
  type: MessageType.CLASSIFICATION_RESULT;
  content: string;
  result?: ResultData;
}

export interface ErrorMessage extends BaseMessage {
  type: MessageType.ERROR;
  error?: string;
}

export interface SystemMessage extends BaseMessage {
  type: MessageType.SYSTEM;
}

export interface VerificationQuestionMessage extends BaseMessage {
  type: MessageType.VERIFICATION_QUESTION;
  sessionId: string;
  question: VerificationQuestion;
}

export interface VerificationResponseMessage extends BaseMessage {
  type: MessageType.VERIFICATION_RESPONSE;
  sessionId: string;
  userResponse: string;
}

export interface UserTextMessage extends BaseMessage {
  type: MessageType.USER_TEXT;
}

export interface AssistantMessage extends BaseMessage {
  type: MessageType.ASSISTANT;
}

export interface ThinkingMessage extends BaseMessage {
  type: MessageType.THINKING;
}

export interface ReasoningStep {
  type:
    | 'thinking'
    | 'text'
    | 'tool_call'
    | 'tool_result'
    | 'phase_change'
    // Deterministic tariff-path streaming (structured steps, not LLM thinking)
    | 'tariff_lookup_step'
    | 'tariff_calculation_step'
    | 'tariff_flag_raised'
    | 'tariff_determination_complete'
    | 'tariff_narration';
  iteration: number;
  phase: string;
  data: Record<string, any>;
}

export interface ReasoningMessage extends BaseMessage {
  type: MessageType.REASONING;
  steps: ReasoningStep[];
  currentPhase: string;
  isComplete?: boolean;
}

export interface AgentQuestionMessage extends BaseMessage {
  type: MessageType.AGENT_QUESTION;
  jobId: string;
  questions: Array<{
    question: string;
    options?: string[];
    context: string;
  }>;
}

export type Message = UserFileMessage | UserTextMessage | AssistantMessage | ThinkingMessage | ProcessingMessage | ResultMessage | ClassificationResultMessage | ErrorMessage | SystemMessage | VerificationQuestionMessage | VerificationResponseMessage | ReasoningMessage | AgentQuestionMessage;

// Chat API Response Types
export interface ImmediateChatResponse {
  success: true;
  type: 'immediate';
  answer: string;
  processing_time_ms: number;
  conversation_id?: string;
  metadata: {
    [key: string]: any;
  };
}

export interface JobChatResponse {
  success: true;
  type: 'job';
  answer: string; // NEW: Agent's response message
  message?: string; // DEPRECATED: Use answer instead
  job_id: string;
  conversation_id?: string;
  tracking: {
    job_id: string;
    status: string;
    progress_url: string;
    status_url: string;
    result_url: string;
  };
  estimated_completion: string;
  metadata: {
    classification_triggered?: boolean; // NEW: Indicates text classification
    queue_position?: number;
    [key: string]: any;
  };
}

export type ChatMessageResponse = ImmediateChatResponse | JobChatResponse;

export interface ChatStore {
  messages: Message[];
  currentJobId: string | null;
  currentConversationId: string | null;
  processingStatus: ProcessingStatus;
  uploadProgress: number;
  error: string | null;
  verificationSessionId: string | null;
  verificationStatus: string | null;
  currentQuestion: VerificationQuestion | null;
  verifiedData: Record<string, any> | null;
  isVerifying: boolean;
  feedbackMap: Map<string, 'up' | 'down'>;
  pendingReclassification: { documentId: string; classificationId: string } | null;
  skipTariff: boolean;
  setSkipTariff: (value: boolean) => void;
  addMessage: (message: Partial<Message>) => void;
  updateLastMessage: (updates: Partial<Message>) => void;
  clearMessages: () => void;
  setProcessingStatus: (status: ProcessingStatus) => void;
  setUploadProgress: (progress: number) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  sendMessage: (message: string, file?: File, options?: { skipTariff?: boolean }, additionalFiles?: File[]) => Promise<void>;
  uploadDocument: (file: File, message?: string) => Promise<void>;
  cancelJob: () => Promise<void>;
  reset: () => void;
  setVerificationSession: (sessionId: string) => void;
  loadVerificationQuestion: () => Promise<void>;
  submitVerificationAnswer: (additionalInfo?: string) => Promise<void>;
  clearVerification: () => void;
  resumePolling: () => void;
  loadConversation: (conversationId: string, userId: string) => Promise<void>;
  startNewConversation: () => void;
  setConversationId: (conversationId: string | null) => void;
  submitFeedback: (classificationId: string, isPositive: boolean) => Promise<void>;
  requestReclassification: (documentId: string, userMessage: string, file?: File) => Promise<void>;
  initiateReclassification: (documentId: string, classificationId: string) => void;
  _applyFinalResult: (result: any) => void;
}
